import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	EventType,
	IChatCommandSurveyMessage,
	IChatCommandSurveyShowData,
	IChatEventFormMessage,
	IChatEventMessage,
	IChatMessagesOut,
	MessageType,
	SenderSource,
} from '@enuan/chat__utils';
import { configurationContext } from '../../../contexts/configuration';
import classNames from 'classnames';
import { getFormLabels, getMessageFormPrefix } from '../../../utils';
import { addFieldDefaultValue } from '../../../utils/addFieldDefaultValue';
import { useBlockMessageClassNames } from 'src/hooks/core/messages.hooks';
import { useSendMessage } from 'src/hooks/core/messages.hooks';
import { CollapsibleForm } from '../../form/CollapsibleForm';
import { useIntl } from 'react-intl';
import { surveyMessages } from '../../screens/Survey/i18n';
import { useMessageContext } from '../../message/message.context';
import { useSelector } from 'react-redux';
import { getSurveyDataEvent } from '../../../reduxStore/selectors';
import { useLogError } from '../../ErrorBoundary/useLogError';
import { ErrorBoundary } from '../../ErrorBoundary';
import { IFormProps } from '../../form';
import { BaseFormMessage } from '../form/BaseFormMessage';
import { defaults as _defaults } from 'lodash';
import type { IOptionalMessageProps } from '../../UI/Message/Message';

export const SurveyMessage = React.forwardRef<
	HTMLElement,
	{ className?: string } & IOptionalMessageProps
>(function SurveyMessage({ className, ...messageProps }, ref) {
	const { survey } = useContext(configurationContext);
	const { message, isLastBlock, isLastMessage } =
		useMessageContext<IChatCommandSurveyMessage<IChatCommandSurveyShowData>>();
	const isNewest = isLastBlock && isLastMessage;
	const messageClassName = useBlockMessageClassNames();
	const logError = useLogError('SurveyMessage', { message });
	const {
		_data: { parameters },
		metadata: { id },
	} = message;
	const { __form_config__ } = parameters ?? {};

	const surveyDataParameters = useSelector(
		(state) => getSurveyDataEvent(state, id)?._data.parameters,
	);

	const formConfig = useMemo(() => {
		if (!__form_config__ && (!survey || !survey.enabled)) return undefined;

		const base = _defaults(__form_config__, survey);
		return {
			...base,
			header: _defaults(__form_config__?.header, survey?.header) ?? {},
			items: addFieldDefaultValue(base?.items ?? [], surveyDataParameters!),
		};
	}, [survey, __form_config__, surveyDataParameters]);

	const intl = useIntl();
	const title = useMemo(
		() =>
			formConfig?.header.title ??
			intl.formatMessage(surveyMessages.header_title),
		[intl, formConfig?.header.title],
	);

	const sendMessage = useSendMessage();
	useEffect(() => {
		if (surveyDataParameters) return;
		if (!isNewest && message._meta.read) return;
		const event: IChatMessagesOut<IChatEventMessage> = {
			_type: MessageType.EVENT,
			_data: { name: EventType.SURVEY_SHOWN },
		};
		sendMessage(event, true).catch((error) =>
			logError('surveyShown', { error, payload: { event } }),
		);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const [readOnly, setReadOnly] = useState(false);
	const isCompleted = !!surveyDataParameters;

	const onSubmitAction = useCallback(
		(values: Record<string, any>) => {
			const formLabels = getFormLabels(formConfig?.items, values);

			const event: IChatMessagesOut<IChatEventFormMessage> = {
				_type: MessageType.EVENT,
				_data: {
					name: EventType.SURVEY_DATA,
					parameters: {
						data: values,
						metadata: {
							command_id: id,
							labels: {
								form: formLabels,
								header: { title, ...formConfig?.header },
							},
						},
					},
				},
			};

			setReadOnly(true);
			sendMessage(event).catch((error) => {
				logError('submit', { error, payload: { event } });
				setReadOnly(false);
			});
			return true;
		},
		[title, formConfig?.items, id, sendMessage, formConfig?.header, logError],
	);

	const meta = useMemo(() => ({ survey, message }), [survey, message]);

	const formProps = useMemo<IFormProps>(
		() => ({
			prefix: getMessageFormPrefix(message!.client_id),
			readOnly: isCompleted || readOnly,
			messages: surveyMessages,
			onSubmitAction: onSubmitAction,
			formConfig: formConfig!,
		}),
		[message, isCompleted, readOnly, onSubmitAction, formConfig],
	);

	useEffect(() => {
		if (formConfig) return;
		if (!isNewest && message._meta.read) return;
		logError('formConfigUnavailable', {
			error: new Error('Form config is unavailable'),
			payload: { id },
		});
	}, [formConfig, id, logError, isNewest, message._meta.read]);

	if (!formProps.formConfig) return null;

	return (
		<ErrorBoundary id="SurveyMessage" meta={meta}>
			<BaseFormMessage
				{...messageProps}
				ref={ref}
				message={message}
				className={classNames(
					className,
					messageClassName,
					'message--survey',
					'message--form',
				)}
				customHeader={null}
				customAvatar={null}
			>
				<CollapsibleForm
					toggable={message!._meta.sent !== true}
					title={title}
					formProps={formProps}
				/>
			</BaseFormMessage>
		</ErrorBoundary>
	);
});
